import { config } from '../config';

/**
 * DISC-7's **quiet-window gate** — one mechanism, two surfaces.
 *
 * `order.ts` answers *what* the order should be (DISC-4 on Discovery, INV-11 on the locked board).
 * This module answers *when* an order may be applied, and it is deliberately surface-agnostic:
 *
 * > **§5.5** — *"This deferral applies to status-driven reflow on the locked board too, not only to
 * > Discovery's count reorder. DISC-7's guarantee is written in §7.3, but LIVE-11 mandates a tap that
 * > both increments and relocates a tile, and LIVE-5's swipe sinks a tile with everything below
 * > shifting up. Without the quiet window, the single most likely double-action in the product —
 * > re-establishing a pattern you had written off, or correcting a mis-swipe — is the one guaranteed
 * > to misfire."*
 *
 * > **§17.1** — *"no row changes rendered position within `REORDER_QUIET_MS` of any tap, on Discovery
 * > **and** the locked board."*
 *
 * ── Why this is its own module rather than an import from `discovery.ts` ─────
 *
 * Build unit U-8 wrote these three functions inside `domain/discovery.ts`, correctly, because
 * Discovery was the only surface that had them. U-9b's locked board needs the identical mechanism,
 * and §17's scope rule is explicit about what to do at that moment: *"where a rule survives only by
 * excluding a file, it is the wrong rule … each had a satisfiable and stronger form one level down."*
 * A board module importing from `discovery` would read as a dependency that does not exist and would
 * invite the one thing the design forbids — a second copy of the gate, of which only one would be
 * tested. The functions moved; nothing about them changed.
 *
 * ── What is NOT here ─────────────────────────────────────────────────────────
 *
 * The **correctness** layer for DISC-7 is not a reorder rule at all. It is `data-hid` resolved at
 * `pointerdown` (§5.1 step 1), which makes *"the second tap cannot increment a different hypothesis"*
 * absolutely true rather than probabilistically true. This module is the **aim** layer: it keeps the
 * tile where the user last saw it.
 */

/** What the gate consults. Both fields are view state; neither is stored. */
export interface QuietGate {
  /** When the most recent tap on this surface completed, or `null` if none has. */
  readonly lastTapTs: number | null;
  /** Whether a finger is currently down anywhere on the surface. */
  readonly pointerDown: boolean;
}

/**
 * DISC-7 — may the target order be applied *right now*?
 *
 * Two conditions, both from §5.5:
 *
 * - **`now − lastTapTs ≥ REORDER_QUIET_MS`.** `≥`, so the boundary instant is allowed: a row moving
 *   at exactly `REORDER_QUIET_MS` has not moved *within* the window.
 * - **no pointer is down.** A settle mid-contact would move the row under a finger that is already
 *   resting on it — the aim layer's whole purpose, and not covered by the time test, because the
 *   contact may have begun long after the previous tap.
 *
 * A `lastTapTs` in the future (a clock stepping backwards) reads as **not allowed**: holding the
 * order still is always safe, and moving a row the user is aiming at is the failure this prevents.
 */
export function reorderAllowed(gate: QuietGate, now: number): boolean {
  if (gate.pointerDown) return false;
  if (gate.lastTapTs === null) return true;
  if (!Number.isFinite(gate.lastTapTs) || !Number.isFinite(now)) return false;
  return now - gate.lastTapTs >= config.REORDER_QUIET_MS;
}

/**
 * DISC-7's deferral: the order to render while the gate is **shut**.
 *
 * Every id that is still present keeps its rendered index. Ids the caller has never displayed are
 * appended at the **tail**, in target order, because appending is the one insertion that relocates
 * nothing. On Discovery that is DISC-5's mid-match addition (§6.1); on the locked board it is
 * LOCK-6's fill of an empty slot, which `board.ts` allocates at the tail for the same reason.
 *
 * Disappearance is handled but is not a live case on either surface: no in-match path deletes a
 * hypothesis, and LOCK-6's swap replaces a position rather than vacating one. An id that vanishes
 * does shift its followers up; that is unavoidable and cannot be deferred, since there is nothing
 * left to render in the gap.
 *
 * @param displayedIds what is on screen now, in rendered order. Empty on first render, which yields
 *   the target order — that is reorder-on-entry, and it needs no separate branch.
 * @param targetIds the order the surface's comparator says it should be.
 */
export function holdOrder(displayedIds: readonly string[], targetIds: readonly string[]): string[] {
  const target = new Set(targetIds);
  const kept = displayedIds.filter((id) => target.has(id));
  const keptSet = new Set(kept);
  return [...kept, ...targetIds.filter((id) => !keptSet.has(id))];
}

/**
 * Arranges rows into an id order, for render.
 *
 * Ids naming no row are dropped and rows named by no id are appended in their incoming order, so a
 * caller that lets the two drift renders every row exactly once rather than losing one. That is not
 * defensive padding: a dropped row on a capture surface is a hypothesis the user cannot tap, and it
 * would be invisible in review because the page — or the grid — would still look full.
 */
export function arrangeBy<T extends { readonly id: string }>(
  rows: readonly T[],
  ids: readonly string[],
): T[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const arranged: T[] = [];
  for (const id of ids) {
    const row = byId.get(id);
    if (row !== undefined) {
      arranged.push(row);
      byId.delete(id);
    }
  }
  for (const row of rows) {
    if (byId.has(row.id)) arranged.push(row);
  }
  return arranged;
}
